import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useNavigate } from 'react-router-dom'
import { addDoc, collection } from 'firebase/firestore'
import { database } from '@/firebase'
import { useCartState } from '@/hooks/context/global/useCartState'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { dealers } from '@/components/dealers/locations'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

export default function QuotePage() {
  const navigate = useNavigate()
  const { items, totalAmount, clearCart } = useCartState()
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    dealerId: '',
    comments: '',
  })

  const total = totalAmount()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (items.length === 0) {
      setError('El carrito está vacío. Añade repuestos antes de solicitar cotización.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const payload = {
        items: items.map((i) => ({
          partId: i.partId,
          name: i.part.name,
          quantity: i.quantity,
          unitPrice: i.part.price ?? 0,
        })),
        total,
        customer: {
          name: form.name,
          email: form.email,
          phone: form.phone,
        },
        dealerId: form.dealerId || null,
        comments: form.comments || null,
        createdAt: new Date(),
        status: 'pending',
      }
      await addDoc(collection(database, 'quote_requests'), payload)
      clearCart()
      setSuccess(true)
      setTimeout(() => navigate('/repuestos'), 3000)
    } catch (err) {
      console.error(err)
      setError('No se pudo enviar la solicitud. Intenta de nuevo.')
    } finally {
      setSubmitting(false)
    }
  }

  if (success) {
    return (
      <>
        <Helmet>
          <title>Solicitud enviada | Repuestos - Oriental Ramirez</title>
        </Helmet>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center shadow-lg">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Solicitud recibida</h1>
            <p className="text-gray-600 mb-6">
              Te contactaremos pronto con la cotización. Serás redirigido al catálogo en unos segundos.
            </p>
            <Button asChild className="bg-[#E10F18] hover:bg-[#c00d15]">
              <Link to="/repuestos">Volver al catálogo</Link>
            </Button>
          </div>
        </div>
      </>
    )
  }

  if (items.length === 0 && !submitting) {
    return (
      <>
        <Helmet>
          <title>Solicitar cotización | Repuestos - Oriental Ramirez</title>
        </Helmet>
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Carrito vacío</h1>
            <p className="text-gray-600 mb-6">Añade repuestos al carrito para solicitar una cotización.</p>
            <Button asChild className="bg-[#E10F18] hover:bg-[#c00d15]">
              <Link to="/repuestos">Ver catálogo</Link>
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Solicitar cotización | Repuestos - Oriental Ramirez</title>
      </Helmet>
      <div className="min-h-screen bg-[#F2F2F6] py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Solicitar cotización</h1>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b bg-muted/30">
              <h2 className="font-semibold text-gray-900 mb-2">Resumen del carrito</h2>
              <ul className="space-y-2 text-sm text-gray-600">
                {items.map((i) => (
                  <li key={i.partId} className="flex justify-between">
                    <span>{i.part.name} × {i.quantity}</span>
                    <span>RD$ {((i.part.price ?? 0) * i.quantity).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 font-semibold text-gray-900 flex justify-between">
                <span>Total</span>
                <span className="text-[#E10F18]">RD$ {total.toLocaleString()}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg">{error}</p>
              )}
              <div>
                <Label htmlFor="name">Nombre completo</Label>
                <Input
                  id="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1"
                  placeholder="Tu nombre"
                />
              </div>
              <div>
                <Label htmlFor="email">Correo electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1"
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div>
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="mt-1"
                  placeholder="(809) 000-0000"
                />
              </div>
              <div>
                <Label>Sucursal preferida</Label>
                <Select
                  value={form.dealerId}
                  onValueChange={(v) => setForm((f) => ({ ...f, dealerId: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar sucursal (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {dealers.map((d) => (
                      <SelectItem key={d.id} value={String(d.id)}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="comments">Comentarios</Label>
                <Textarea
                  id="comments"
                  value={form.comments}
                  onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                  className="mt-1 min-h-[80px]"
                  placeholder="Indica si necesitas entrega, plazo, etc."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-[#E10F18] hover:bg-[#c00d15]"
                >
                  {submitting ? 'Enviando...' : 'Enviar solicitud'}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link to="/repuestos">Cancelar</Link>
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
